import React, { useEffect, useRef, useState, useContext } from 'react';
import Modal from '../Modal';
import {
	GlobalWorkerOptions,
	getDocument,
	PDFDocumentProxy,
	PageViewport,
} from 'pdfjs-dist';
import { KeyboardContext, KeyboardTrigger } from '../Layout';
import { WrappedInput } from '../Settings';
import { Scroll } from '../General';
import DirectionalButton from '../shared/DirectionalButton';

interface PdfViewerProps {
	onRequestClose: () => void;
	pdfUrl: string;
	initialPage?: number;
	searchQuery?: string;
	showNavigation?: boolean;
	showSearch?: boolean;
	skipToC?: boolean;
	setLastPage?: (lastPage: number) => void;
}

const PDFModal: React.FC<PdfViewerProps> = ({
	onRequestClose,
	pdfUrl,
	initialPage = 1,
	searchQuery = '',
	skipToC = false,
	showNavigation = true,
	showSearch = true,
	setLastPage,
}) => {
	const modalRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
	const [numPages, setNumPages] = useState<number>(0);
	const [pageNum, setPageNum] = useState<number>(initialPage);
	const [rendering, setRendering] = useState<boolean>(false);
	const [query, setQuery] = useState<string>('');
	const [searching, setSearching] = useState<boolean>(false);
	const [searchResults, setSearchResults] = useState<
		{
			page: number;
			matches: { x: number; y: number; width: number; height: number }[];
		}[]
	>([]);
	const [currentSearchIndex, setCurrentSearchIndex] = useState<number>(0);

	const renderTaskRef = useRef<any>(null);
	const lKeyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);

	useEffect(() => {
		if (!pdfUrl) return;
		GlobalWorkerOptions.workerSrc = '/static/workers/pdf.worker.min.js';
		const loadPdf = async () => {
			try {
				const loadingTask = getDocument(pdfUrl);
				const pdf = await loadingTask.promise;
				setPdfDoc(pdf);
				setNumPages(pdf.numPages);
				if (!searchQuery) {
					setPageNum(initialPage);
				}
			} catch (error) {
				console.error('Error loading PDF:', error);
			}
		};

		loadPdf();
	}, [pdfUrl, initialPage, searchQuery]);

	useEffect(() => {
		if (pdfDoc) {
			setQuery(searchQuery || '');
		}
	}, [pdfDoc, searchQuery]);

	useEffect(() => {
		if (!pdfDoc || !query) return;
		handleSearch();
	}, [pdfDoc, query]);

	useEffect(() => {
		if (pdfDoc) {
			renderPage(pageNum);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pdfDoc, pageNum]);

	const forceRenderPage = async (
		num: number,
		forcedResults?: typeof searchResults
	) => {
		if (!pdfDoc) return;
		if (renderTaskRef.current) {
			renderTaskRef.current.cancel();
		}
		setRendering(true);
		try {
			const page = await pdfDoc.getPage(num);
			const scale = 1.5;
			const viewport = page.getViewport({ scale });

			const canvas = canvasRef.current!;
			const context = canvas.getContext('2d')!;
			canvas.height = viewport.height;
			canvas.width = viewport.width;

			const renderContext = {
				canvasContext: context,
				viewport,
			};
			const task = page.render(renderContext);
			renderTaskRef.current = task;
			await task.promise;

			highlightMatchesOnPage(num, context, viewport, scale, forcedResults);
		} catch (error: any) {
			if (error?.name !== 'RenderingCancelledException') {
				console.error('Error rendering page:', error);
			}
		} finally {
			setRendering(false);
			renderTaskRef.current = null;
		}
	};
	const highlightMatchesOnPage = (
		pageNum: number,
		context: CanvasRenderingContext2D,
		viewport: PageViewport,
		scale: number,
		forcedResults?: typeof searchResults
	) => {
		const results = forcedResults ?? searchResults;
		const pageMatches = results.find(r => r.page === pageNum)?.matches || [];

		pageMatches.forEach(({ x, y, width, height }) => {
			context.fillStyle = 'rgba(121, 222, 247, 0.5)';
			const highlightX = x * scale;
			const highlightY = viewport.height - y * scale - height * scale;
			context.fillRect(highlightX, highlightY, width * scale, height * scale);
		});
	};

	const renderPage = async (num: number) => {
		await forceRenderPage(num);
	};

	const handleSearch = async () => {
		if (!pdfDoc || !query) return;
		setSearching(true);
		setSearchResults([]);
		setCurrentSearchIndex(0);

		const results: {
			page: number;
			matches: { x: number; y: number; width: number; height: number }[];
		}[] = [];

		try {
			for (let i = 1; i <= pdfDoc.numPages; i++) {
				const page = await pdfDoc.getPage(i);
				const textContent = await page.getTextContent();
				const matches: { x: number; y: number; width: number; height: number }[] =
					[];

				textContent.items.forEach((item: any) => {
					const str = item.str.toLowerCase();
					const queryStr = query.toLowerCase();
					if (str.includes(queryStr)) {
						const transform = item.transform;
						matches.push({
							x: transform[4],
							y: transform[5],
							width: item.width,
							height: item.height,
						});
					}
				});

				if (matches.length > 0) {
					results.push({ page: i, matches });
				}
			}
			setSearchResults(results);

			if (results.length > 0) {
				const initialIndex = skipToC ? 1 : 0;
				setCurrentSearchIndex(initialIndex);

				const newPage = results[initialIndex]?.page || results[0].page;

				if (newPage !== pageNum) {
					setPageNum(newPage);
				} else {
					await forceRenderPage(newPage);
				}
			} else {
				if (pageNum !== 1) {
					setPageNum(1);
				} else {
					await forceRenderPage(1);
				}
			}
		} catch (error) {
			console.error('Error during search:', error);
		} finally {
			setSearching(false);
		}
	};

	const handleFindNext = async () => {
		if (searchResults.length > 0) {
			const nextIndex = (currentSearchIndex + 1) % searchResults.length;
			setCurrentSearchIndex(nextIndex);
			const newPage = searchResults[nextIndex].page;
			if (newPage !== pageNum) {
				setPageNum(newPage);
			} else {
				await forceRenderPage(newPage);
			}
		}
	};

	const handleFindPrevious = async () => {
		if (searchResults.length > 0) {
			const prevIndex =
				(currentSearchIndex - 1 + searchResults.length) % searchResults.length;
			setCurrentSearchIndex(prevIndex);
			const newPage = searchResults[prevIndex].page;
			if (newPage !== pageNum) {
				setPageNum(newPage);
			} else {
				await forceRenderPage(newPage);
			}
		}
	};
	const handleClearSearch = async () => {
		setQuery('');
		setSearching(false);
		setSearchResults([]);
		setCurrentSearchIndex(0);
		await forceRenderPage(pageNum, []);
	};

	const roundButtonStyle = {
		borderRadius: '50%',
		width: '80px',
		height: '80px',
		border: 'none',
		padding: '0',
		color: 'white',
	};

	return (
		<Modal
			close={() => {
				if (setLastPage) {
					setLastPage(pageNum);
				}
				onRequestClose();
			}}
		>
			<div
				ref={modalRef}
				style={{ textAlign: 'center', zIndex: '9', backgroundColor: 'white' }}
			>
				<Scroll>
					<div>
						{showNavigation && (
							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'center',
									gap: '1em',
									alignItems: 'center',
									paddingBottom: '4em',
									paddingTop: '4em',
									height: '80px',
								}}
							>
								<DirectionalButton onClick={() => setPageNum(1)} disabled={pageNum === 1 || rendering} direction='start' size='large'/>
								<DirectionalButton onClick={() => setPageNum(Math.max(1, pageNum - 1))} disabled={pageNum <= 1 || rendering} direction='left' size='large' />
								<div className="flex-row align-center justify-center width-25">
									<span className="font-2 dark-blue">Page</span>
									<div className="padding width-40">
										<WrappedInput
											field={`Page Number`}
											value={pageNum}
											unitsSystem={void 0}
											setter={pNewValue => setPageNum(Number(pNewValue))}
											triggerKeyboard={lKeyboardTrigger}
										/>
									</div>
									<span className="font-2 dark-blue">of {numPages}</span>
								</div>
								<DirectionalButton onClick={() => setPageNum(Math.min(numPages, pageNum + 1))} disabled={pageNum >= numPages || rendering} direction='right' size='large' />
								<DirectionalButton onClick={() => setPageNum(numPages)} disabled={pageNum >= numPages || rendering} direction='end' size='large' />
							</div>
						)}

						{showSearch && (
							<div
								id="pdfViewerContainer"
								style={{
									display: 'flex',
									justifyContent: 'center',
									paddingBottom: '4em',
								}}
							>
								<div
									style={{
										backgroundColor: '#1c344f',
										display: 'flex',
										alignItems: 'center',
										padding: '1em',
										borderRadius: '10px',
									}}
								>
									<WrappedInput
										field={'PDF Search'}
										value={query}
										setter={pNewValue => setQuery(String(pNewValue))}
										triggerKeyboard={lKeyboardTrigger}
									/>
									<div
										style={{
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'center',
											backgroundColor: '#1c344f',
											border: '1px solid rgb(204, 204, 204);',
											gap: '10px',
											height: '100%',
										}}
									>
										<p style={{ color: 'white', paddingLeft: '1em' }}>{`${
											searchResults.length === 0 ? '0' : currentSearchIndex + 1
										}/${searchResults.length}`}</p>
										<button
											className="searchButton"
											style={{ fontSize: '3vw' }}
											onClick={handleFindPrevious}
										>
											&#9650;
										</button>
										<button
											className="searchButton"
											style={{
												fontSize: '3vw',
											}}
											onClick={handleFindNext}
										>
											&#9660;
										</button>
										<button
											className="clearSearchButton"
											style={{
												fontSize: '3vw',
												padding: '0.25em',
											}}
											onClick={handleClearSearch}
										>
											x
										</button>
									</div>
								</div>
							</div>
						)}

						{rendering && <div>Rendering Page...</div>}
						{searching && <div>Searching...</div>}
					</div>
					<canvas
						ref={canvasRef}
						style={{ border: '1px solid rgba(28, 52, 79, 0.4)' }}
					/>
				</Scroll>
			</div>
		</Modal>
	);
};

export default PDFModal;
